import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { type DownloadableFile,useDownload } from '@/hooks/useDownload';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import type {
  ReadTemplateSummary,
  TemplateFormat,
  TemplateListApiArg,
} from '@/services/api/generated/templatesV2Service';
import {
  enhancedApi as templatesV2Api,
  useTemplateSetDefaultMutation,
  useTemplateUnsetDefaultMutation,
} from '@/services/api/generated/templatesV2Service';
import {
  useTemplateBulkArchiveMutation,
  useTemplateListMultiQuery,
} from '@/services/api/templatesV2ServiceEnhanced';
import { store } from '@/state/state';

import { GLOBAL_FIRM_FILTER_VALUE } from './constants';
import { TemplatesOperatorTable } from './TemplatesOperatorTable';
import {
  DEFAULT_FILTER_STATE,
  DEFAULT_PAGINATION,
  type TemplateFilterState,
  type TemplatePaginationState,
} from './types';

interface TemplatesV2PageProps {
  /** If provided, locks the view to a single firm */
  firmId?: string;
}

type TemplateCategoryValue = NonNullable<TemplateListApiArg['category']>;
type TemplateFormatValue = NonNullable<TemplateListApiArg['templateFormat']>;

const TEMPLATE_SCOPE_VALUES = new Set<NonNullable<TemplateListApiArg['scope']>>(
  ['FIRM', 'GLOBAL'],
);

const TEMPLATE_STATUS_VALUES = new Set<
  NonNullable<TemplateListApiArg['status']>
>(['ACTIVE', 'ARCHIVED', 'SUPERSEDED']);

const TEMPLATE_CATEGORY_VALUES = new Set<TemplateCategoryValue>([
  'AFFIDAVIT',
  'CLIENT_COMMUNICATION',
  'DROP_LETTER',
  'FIRM_AUTHORIZATION',
  'FOIA_LETTER',
  'HIPAA_AUTHORIZATION',
  'HIPAA_AUTHORIZATION_RETAINER',
  'HIPAA_COVERSHEET',
  'JOINT_VENTURE_NOTICE',
  'LETTER_OF_PROTECTION',
  'LIEN_NOTICE',
  'LOR_FIRST_PARTY',
  'LOR_GENERIC',
  'LOR_HEALTH',
  'LOR_THIRD_PARTY',
  'MEDICAL_RECORDS_REQUEST',
  'OTHER',
  'PIP_DEMAND',
  'PRESERVATION_LETTER',
  'RETAINER',
  'WELCOME_LETTER',
]);

const TEMPLATE_FORMAT_VALUES = new Set<TemplateFormatValue>([
  'DOCX',
  'EXTERNAL',
  'HTML',
  'PDF',
  'SMS',
]);

const SORTABLE_FIELDS = new Set([
  'name',
  'firm',
  'category',
  'format',
  'is_default',
  'modified_at',
]);

const DEFAULT_SORT_FIELD = 'modified_at';
const DEFAULT_SORT_DIRECTION: 'asc' | 'desc' = 'desc';
const FORMAT_DOWNLOAD_EXTENSIONS: Record<TemplateFormat, string> = {
  DOCX: 'docx',
  PDF: 'pdf',
  HTML: 'html',
  SMS: 'txt',
  EXTERNAL: 'bin',
};

const sanitizeFileNameSegment = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'template';
  }

  const normalized = trimmed
    .replace(/[<>:"/\\|?*\p{Cc}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);

  return normalized || 'template';
};

const inferExtensionFromUrl = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    const fileName = parsed.pathname.split('/').pop() ?? '';
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension || extension.length > 10 || extension === fileName.toLowerCase()) {
      return null;
    }
    return extension;
  } catch {
    return null;
  }
};

const resolveDownloadExtension = (
  format: TemplateFormat | undefined,
  fileUrl: string | null | undefined,
): string => {
  const fromUrl = inferExtensionFromUrl(fileUrl);
  if (fromUrl) {
    return fromUrl;
  }
  return format ? FORMAT_DOWNLOAD_EXTENSIONS[format] : 'bin';
};

const makeUniqueName = (
  baseName: string,
  extension: string,
  seenNames: Map<string, number>,
): string => {
  const key = `${baseName}.${extension}`.toLowerCase();
  const existing = seenNames.get(key) ?? 0;
  seenNames.set(key, existing + 1);
  return existing === 0 ? baseName : `${baseName} (${existing + 1})`;
};

const parseBooleanParam = (value: string | null): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const parsePositiveIntParam = (value: string | null, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : fallback;
};

const parseNonNegativeIntParam = (
  value: string | null,
  fallback: number,
): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  const normalized = Math.floor(parsed);
  return normalized >= 0 ? normalized : fallback;
};

const parseScopeParam = (
  value: string | null,
): TemplateListApiArg['scope'] | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.toUpperCase();
  return TEMPLATE_SCOPE_VALUES.has(
    normalized as NonNullable<TemplateListApiArg['scope']>,
  )
    ? (normalized as TemplateListApiArg['scope'])
    : undefined;
};

const parseStatusParam = (
  value: string | null,
): TemplateListApiArg['status'] | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.toUpperCase();
  return TEMPLATE_STATUS_VALUES.has(
    normalized as NonNullable<TemplateListApiArg['status']>,
  )
    ? (normalized as TemplateListApiArg['status'])
    : undefined;
};

const parseMultiQueryParam = (
  searchParams: URLSearchParams,
  key: string,
): string[] => {
  const values = searchParams.getAll(key);
  if (values.length === 0) {
    const single = searchParams.get(key);
    if (!single) {
      return [];
    }
    values.push(single);
  }

  const tokens = values
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

  return Array.from(new Set(tokens));
};

const parseCategoryParams = (
  searchParams: URLSearchParams,
): TemplateCategoryValue[] =>
  parseMultiQueryParam(searchParams, 'category').filter((value) =>
    TEMPLATE_CATEGORY_VALUES.has(value as TemplateCategoryValue),
  ) as TemplateCategoryValue[];

const parseFormatParams = (
  searchParams: URLSearchParams,
): TemplateFormatValue[] =>
  [
    ...parseMultiQueryParam(searchParams, 'template_format'),
    ...parseMultiQueryParam(searchParams, 'format'),
  ].filter((value, index, allValues) => allValues.indexOf(value) === index).filter((value) =>
    TEMPLATE_FORMAT_VALUES.has(value as TemplateFormatValue),
  ) as TemplateFormatValue[];

const parseFirmIdsParam = (searchParams: URLSearchParams): string[] => {
  const fromSnake = parseMultiQueryParam(searchParams, 'firm_id');
  const fromCamel = parseMultiQueryParam(searchParams, 'firmId');
  return Array.from(new Set([...fromSnake, ...fromCamel]));
};

const parseOrderingParam = (
  ordering: string | null,
): { field: string | undefined; direction: 'asc' | 'desc' } => {
  const fallbackSort = {
    field: DEFAULT_SORT_FIELD,
    direction: DEFAULT_SORT_DIRECTION,
  } as const;

  if (!ordering) {
    return fallbackSort;
  }

  const primaryToken = ordering.split(',')[0]?.trim();
  if (!primaryToken) {
    return fallbackSort;
  }

  if (primaryToken.startsWith('-')) {
    const field = primaryToken.slice(1).trim();
    return field && SORTABLE_FIELDS.has(field)
      ? { field, direction: 'desc' }
      : fallbackSort;
  }

  return SORTABLE_FIELDS.has(primaryToken)
    ? { field: primaryToken, direction: 'asc' }
    : fallbackSort;
};

const setOrDeleteSearchParam = (
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | null | undefined,
) => {
  if (value === undefined || value === null || value === '') {
    params.delete(key);
    return;
  }

  params.set(key, String(value));
};

const setOrDeleteListSearchParam = (
  params: URLSearchParams,
  key: string,
  values: string[] | undefined,
) => {
  params.delete(key);
  if (!values || values.length === 0) {
    return;
  }

  params.set(key, values.join(','));
};

export const TemplatesV2Page = ({ firmId }: TemplatesV2PageProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialOrdering = parseOrderingParam(searchParams.get('ordering'));
  const initialScope = parseScopeParam(searchParams.get('scope'));
  const initialStatus = parseStatusParam(searchParams.get('status'));
  const initialFirmIds = parseFirmIdsParam(searchParams);
  const initialIsDefault = parseBooleanParam(
    searchParams.get('is_default') ?? searchParams.get('isDefault'),
  );
  const initialSearch = searchParams.get('search') ?? '';
  const initialCategories = parseCategoryParams(searchParams);
  const initialFormats = parseFormatParams(searchParams);

  // Filter state — defaults to ACTIVE status
  const [filters, setFilters] = useState<TemplateFilterState>(() => ({
    ...DEFAULT_FILTER_STATE,
    categories: initialCategories,
    formats: initialFormats,
    scope: firmId ? 'FIRM' : initialScope,
    status: initialStatus ?? DEFAULT_FILTER_STATE.status,
    isDefault: initialIsDefault,
    firmIds: firmId ? [firmId] : initialFirmIds,
  }));

  // Pagination
  const [pagination, setPagination] = useState<TemplatePaginationState>(() => ({
    limit: parsePositiveIntParam(
      searchParams.get('limit'),
      DEFAULT_PAGINATION.limit,
    ),
    offset: parseNonNegativeIntParam(
      searchParams.get('offset'),
      DEFAULT_PAGINATION.offset,
    ),
  }));

  // Sort
  const [sortField, setSortField] = useState<string | undefined>(
    initialOrdering.field,
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    initialOrdering.direction,
  );

  // Search (debounced)
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // UI state
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const archivedOnly = filters.status === 'ARCHIVED';
  const ordering = sortField
    ? (`${sortDirection === 'desc' ? '-' : ''}${sortField}` as TemplateListApiArg['ordering'])
    : undefined;

  // Build query args
  const queryArgs = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      categories: filters.categories.length > 0 ? filters.categories : undefined,
      formats: filters.formats.length > 0 ? filters.formats : undefined,
      scope: filters.scope,
      status: filters.status,
      isDefault: filters.isDefault,
      firmIds: filters.firmIds.length > 0 ? filters.firmIds : undefined,
      ordering,
      limit: pagination.limit,
      offset: pagination.offset,
    }),
    [filters, debouncedSearch, ordering, pagination],
  );

  const {
    categories: queryCategories,
    firmIds: queryFirmIds,
    formats: queryFormats,
    isDefault: queryIsDefault,
    limit: queryLimit,
    offset: queryOffset,
    ordering: queryOrdering,
    scope: queryScope,
    search: querySearch,
    status: queryStatus,
  } = queryArgs;
  const currentUrlSearch = searchParams.toString();

  useEffect(() => {
    const nextParams = new URLSearchParams(currentUrlSearch);
    const managedKeys = [
      'search',
      'category',
      'format',
      'template_format',
      'scope',
      'status',
      'is_default',
      'isDefault',
      'firm_id',
      'firmId',
      'ordering',
      'limit',
      'offset',
    ];

    managedKeys.forEach((key) => nextParams.delete(key));

    setOrDeleteSearchParam(nextParams, 'search', querySearch);
    setOrDeleteListSearchParam(nextParams, 'category', queryCategories);
    setOrDeleteListSearchParam(nextParams, 'template_format', queryFormats);
    setOrDeleteSearchParam(nextParams, 'scope', queryScope);
    setOrDeleteSearchParam(nextParams, 'status', queryStatus);
    setOrDeleteSearchParam(nextParams, 'is_default', queryIsDefault);
    setOrDeleteListSearchParam(nextParams, 'firm_id', queryFirmIds);
    setOrDeleteSearchParam(nextParams, 'ordering', queryOrdering);
    setOrDeleteSearchParam(nextParams, 'limit', queryLimit);
    setOrDeleteSearchParam(nextParams, 'offset', queryOffset);

    const nextSearch = nextParams.toString();
    if (nextSearch !== currentUrlSearch) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    currentUrlSearch,
    queryCategories,
    queryFirmIds,
    queryFormats,
    queryIsDefault,
    queryLimit,
    queryOffset,
    queryOrdering,
    queryScope,
    querySearch,
    queryStatus,
    setSearchParams,
  ]);

  const { data, isLoading } = useTemplateListMultiQuery(queryArgs);
  const { data: firmsData } = useGetFirmsQuery({});

  const [setDefault] = useTemplateSetDefaultMutation();
  const [unsetDefault] = useTemplateUnsetDefaultMutation();
  const [bulkArchiveTemplates] = useTemplateBulkArchiveMutation();
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [isPreparingBulkDownload, setIsPreparingBulkDownload] = useState(false);
  const { downloadFiles, isDownloading: isBulkDownloading } = useDownload();
  const isBulkDownloadBusy = isPreparingBulkDownload || isBulkDownloading;

  // Bulk confirmation dialogs
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);

  const templates = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  // Build firm ID -> name lookup map
  const firmNames = useMemo(() => {
    if (!firmsData) return {};
    return firmsData.reduce<Record<string, string>>((map, firm) => {
      map[firm.id] = firm.name;
      return map;
    }, {});
  }, [firmsData]);

  const firmFilterOptions = useMemo(
    () => [
      {
        value: GLOBAL_FIRM_FILTER_VALUE,
        label: 'Global (System)',
      },
      ...(firmsData ?? [])
        .map((firm) => ({
          value: firm.id,
          label: firm.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [firmsData],
  );

  useEffect(() => {
    if (searchInput === debouncedSearch) {
      return undefined;
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPagination((prev) => ({ ...prev, offset: 0 }));
    }, 300);

    return () => clearTimeout(searchDebounceRef.current);
  }, [debouncedSearch, searchInput]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [
    debouncedSearch,
    filters.categories,
    filters.firmIds,
    filters.formats,
    filters.isDefault,
    filters.scope,
    filters.status,
    pagination.offset,
    pagination.limit,
  ]);

  const handleSortChange = useCallback(
    (field: string | undefined, direction?: 'asc' | 'desc') => {
      setPagination((prev) => ({ ...prev, offset: 0 }));

      if (!field || !direction) {
        setSortField(undefined);
        setSortDirection('asc');
        return;
      }

      setSortField(field);
      setSortDirection(direction);
    },
    [],
  );

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPagination({ limit: size, offset: 0 });
  }, []);

  const handleFirmFilterChange = useCallback(
    (selectedFirmValues: string[]) => {
      if (firmId) {
        return;
      }

      setPagination((prev) => ({ ...prev, offset: 0 }));
      setFilters((prev) => {
        return {
          ...prev,
          firmIds: selectedFirmValues,
          scope: selectedFirmValues.length > 0 ? undefined : prev.scope,
        };
      });
    },
    [firmId],
  );

  const handleScopeQuickFilterChange = useCallback(
    (scope: TemplateListApiArg['scope'] | undefined) => {
      if (firmId) {
        return;
      }

      setPagination((prev) => ({ ...prev, offset: 0 }));
      setFilters((prev) => ({
        ...prev,
        scope,
        firmIds: [],
      }));
    },
    [firmId],
  );

  const handleCategoryFilterChange = useCallback(
    (categories: TemplateCategoryValue[]) => {
      setPagination((prev) => ({ ...prev, offset: 0 }));
      setFilters((prev) => ({
        ...prev,
        categories,
      }));
    },
    [],
  );

  const handleFormatFilterChange = useCallback(
    (formats: TemplateFormatValue[]) => {
      setPagination((prev) => ({ ...prev, offset: 0 }));
      setFilters((prev) => ({
        ...prev,
        formats,
      }));
    },
    [],
  );

  const handleDefaultFilterChange = useCallback(
    (isDefault: boolean | undefined) => {
      setPagination((prev) => ({ ...prev, offset: 0 }));
      setFilters((prev) => ({
        ...prev,
        isDefault,
      }));
    },
    [],
  );

  const handleArchivedOnlyChange = useCallback((checked: boolean) => {
    setPagination((prev) => ({ ...prev, offset: 0 }));
    setFilters((prev) => ({
      ...prev,
      status: checked ? 'ARCHIVED' : 'ACTIVE',
    }));
  }, []);

  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchInput.trim().length > 0;
    const hasCategory = filters.categories.length > 0;
    const hasFormat = filters.formats.length > 0;
    const hasFirm = !firmId && filters.firmIds.length > 0;
    const hasDefault = filters.isDefault !== undefined;
    const hasArchived = archivedOnly;

    const hasScope = !firmId && !!filters.scope;

    return (
      hasSearch ||
      hasCategory ||
      hasFormat ||
      hasFirm ||
      hasDefault ||
      hasArchived ||
      hasScope
    );
  }, [
    archivedOnly,
    filters.categories,
    filters.firmIds,
    filters.formats,
    filters.isDefault,
    filters.scope,
    firmId,
    searchInput,
  ]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    setPagination((prev) => ({ ...prev, offset: 0 }));
    setFilters((prev) => ({
      ...prev,
      categories: [],
      formats: [],
      isDefault: undefined,
      status: 'ACTIVE',
      scope: firmId ? 'FIRM' : undefined,
      firmIds: firmId ? [firmId] : [],
    }));
  }, [firmId]);

  const handleRowClick = useCallback(
    (row: ReadTemplateSummary) => {
      const path = `/templates-v2/${row.id}`;
      const params = new URLSearchParams(currentUrlSearch);
      if (firmId) {
        params.set('firmContext', firmId);
      }
      const search = params.toString();
      navigate(search ? `${path}?${search}` : path);
    },
    [currentUrlSearch, firmId, navigate],
  );

  const handleToggleDefault = useCallback(
    async (row: ReadTemplateSummary) => {
      try {
        if (row.is_default) {
          await unsetDefault({ id: row.id as string }).unwrap();
          toast.success('Template is no longer default');
        } else {
          await setDefault({ id: row.id as string }).unwrap();
          toast.success('Template set as default');
        }
      } catch {
        toast.error('Failed to update default status');
      }
    },
    [setDefault, unsetDefault],
  );

  // Bulk action handlers
  const handleBulkDownload = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0 || isBulkDownloadBusy) {
      return;
    }

    setIsPreparingBulkDownload(true);

    try {
      type DownloadResolution =
        | {
            status: 'ready';
            id: string;
            name: string;
            format: TemplateFormat;
            url: string;
          }
        | {
            status: 'skipped';
            id: string;
            reason: string;
          };

      const resolutions = await Promise.all(
        ids.map(async (id): Promise<DownloadResolution> => {
          try {
            const template = await store
              .dispatch(
                templatesV2Api.endpoints.templateRetrieve.initiate(
                  { id },
                  { subscribe: false },
                ),
              )
              .unwrap();

            if (!template.file_url) {
              return {
                status: 'skipped',
                id,
                reason: 'No downloadable file',
              };
            }

            return {
              status: 'ready',
              id,
              name: template.display_name || template.name,
              format: template.format,
              url: template.file_url,
            };
          } catch {
            return {
              status: 'skipped',
              id,
              reason: 'Failed to load template',
            };
          }
        }),
      );

      const ready = resolutions.filter(
        (resolution): resolution is Extract<DownloadResolution, { status: 'ready' }> =>
          resolution.status === 'ready',
      );
      const skipped = resolutions.filter(
        (resolution) => resolution.status === 'skipped',
      );

      if (ready.length === 0) {
        toast.error('No downloadable files found in selected templates');
        return;
      }

      const seenNames = new Map<string, number>();
      const filesToDownload: DownloadableFile[] = ready.map((item) => {
        const baseName = sanitizeFileNameSegment(item.name || `template-${item.id}`);
        const extension = resolveDownloadExtension(item.format, item.url);
        const uniqueName = makeUniqueName(baseName, extension, seenNames);

        return {
          downloadUrl: item.url,
          name: uniqueName,
          fileExtension: extension,
        };
      });

      if (skipped.length > 0) {
        toast.warning(
          `${filesToDownload.length} template${filesToDownload.length !== 1 ? 's' : ''} ready to download, ${skipped.length} skipped`,
        );
      }

      const zipDate = new Date().toISOString().slice(0, 10);
      await downloadFiles(filesToDownload, `templates-${zipDate}.zip`);
    } finally {
      setIsPreparingBulkDownload(false);
    }
  }, [downloadFiles, isBulkDownloadBusy, selectedRows]);

  const handleBulkArchive = useCallback(() => {
    setBulkArchiveDialogOpen(true);
  }, []);

  const handleBulkArchiveConfirm = useCallback(async () => {
    const ids = Array.from(selectedRows);
    setIsBulkArchiving(true);
    try {
      const response = await bulkArchiveTemplates({ ids }).unwrap();
      const succeeded = response.processed_count;
      const failed = response.skipped.length;

      if (succeeded > 0 && failed > 0) {
        toast.warning(
          `Archived ${succeeded} template${succeeded !== 1 ? 's' : ''}, ${failed} skipped`,
        );
      } else if (succeeded > 0) {
        toast.success(
          `${succeeded} template${succeeded !== 1 ? 's' : ''} archived`,
        );
      } else if (failed > 0) {
        toast.error(`No templates archived (${failed} skipped)`);
      } else {
        toast.error('Failed to archive selected templates');
      }

      if (succeeded > 0) {
        setSelectedRows(new Set());
      }
    } catch {
      toast.error('Failed to archive selected templates');
    } finally {
      setIsBulkArchiving(false);
      setBulkArchiveDialogOpen(false);
    }
  }, [selectedRows, bulkArchiveTemplates]);

  // Include firmContext in the search string passed to the operator table
  // so that ctrl+click / middle-click opens the detail page with firm context.
  const firmDetailSearch = useMemo(() => {
    if (!firmId) return currentUrlSearch;
    const params = new URLSearchParams(currentUrlSearch);
    params.set('firmContext', firmId);
    return params.toString();
  }, [currentUrlSearch, firmId]);

  const pageTitle = firmId ? 'Firm Templates' : 'Templates';

  return (
    <div className="flex h-full flex-col p-4">
      <TemplatesOperatorTable
        title={pageTitle}
        subtitle="Manage document templates across formats and categories"
        data={templates}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pagination.limit}
        searchValue={searchInput}
        archivedOnly={archivedOnly}
        selectedRows={selectedRows}
        sortField={sortField}
        sortDirection={sortDirection}
        firmFilterValues={filters.firmIds}
        firmOptions={firmId ? [] : firmFilterOptions}
        scopeFilterValue={firmId ? 'FIRM' : filters.scope}
        categoryFilterValues={filters.categories}
        formatFilterValues={filters.formats}
        defaultFilterValue={filters.isDefault}
        isBulkArchiving={isBulkArchiving}
        canClearFilters={hasActiveFilters}
        onSearchChange={handleSearchChange}
        onArchivedOnlyChange={handleArchivedOnlyChange}
        onClearFilters={handleClearFilters}
        onScopeFilterChange={
          firmId ? undefined : handleScopeQuickFilterChange
        }
        firmId={firmId}
        onSelectionChange={setSelectedRows}
        onBulkDownloadClick={handleBulkDownload}
        onBulkArchiveClick={handleBulkArchive}
        onSortChange={handleSortChange}
        onFirmFilterChange={handleFirmFilterChange}
        onCategoryFilterChange={handleCategoryFilterChange}
        onFormatFilterChange={handleFormatFilterChange}
        onDefaultFilterChange={handleDefaultFilterChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
        onToggleDefault={handleToggleDefault}
        firmNames={firmNames}
        detailSearch={firmDetailSearch}
        isBulkDownloading={isBulkDownloadBusy}
      />
      {/* Bulk Archive Confirmation */}
      <AlertDialog
        open={bulkArchiveDialogOpen}
        onOpenChange={setBulkArchiveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Templates</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive {selectedRows.size} selected
              template
              {selectedRows.size !== 1 ? 's' : ''}? Archived templates can be
              restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkArchiving}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkArchiveConfirm}
              disabled={isBulkArchiving}
            >
              {isBulkArchiving ? 'Archiving...' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
